import React, {
  ReactNode,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from 'react';
import { IntlProvider as I18nWrapper, useIntl } from 'react-intl';

import { getCimodeMode, subscribeToCimode } from '~/i18n/cimode';
import { intlService } from '~/i18n/IntlService';

// QA debug mode: `cimode` renders message ids verbatim instead of translations,
// `cimode+` prefixes them with `i18n:` to make hardcoded strings easier to spot.
// Mirrors react-i18next's `cimode`. Toggling is reactive via subscribeToCimode.
//
// react-intl gates translation lookup on `hasOwnProperty(id)`, so the Proxy
// must trap `has` and `getOwnPropertyDescriptor` in addition to `get`.
// Without those traps, react-intl thinks every id is missing and falls back
// to `defaultMessage`.
const makeCimodeMessages = (transform: (key: string) => string) =>
  new Proxy({} as Record<string, string>, {
    get: (_, key) => (typeof key === 'string' ? transform(key) : undefined),
    has: (_, key) => typeof key === 'string',
    getOwnPropertyDescriptor: (_, key) =>
      typeof key === 'string'
        ? {
            value: transform(key),
            writable: false,
            enumerable: true,
            configurable: true,
          }
        : undefined,
  });

const cimodeMessages = makeCimodeMessages((key) => key);
const cimodePlusMessages = makeCimodeMessages((key) => `i18n:${key}`);

interface Props {
  children: ReactNode;
  currency: string;
  language: string;
  messages: any;
}

const IntlServiceInitializer = () => {
  const intl = useIntl();

  useEffect(() => {
    intlService.init(intl);
    return () => intlService.reset();
  }, [intl]);

  return null;
};

export const I18nProvider = ({
  children,
  //currency,
  language,
  messages,
}: Props) => {
  // TODO: once we have a BE unified currency conversion, we can use the currency prop by adding it to CurrencyContext.Provider

  // NOTE: We pass 'language' to IntlProvider's locale for correct translation message lookups.
  // The formatting locale is retrieved directly from Redux in useFormatDate/useFormatNumber via useSelector.
  // This separates language (translations) from locale (date/number formatting).

  const cimodeMode = useSyncExternalStore(subscribeToCimode, getCimodeMode);
  const resolvedMessages = useMemo(() => {
    if (cimodeMode === 'cimode') return cimodeMessages;
    if (cimodeMode === 'cimode+') return cimodePlusMessages;
    return messages;
  }, [messages, cimodeMode]);

  return (
    <I18nWrapper
      locale={language}
      defaultLocale="en"
      messages={resolvedMessages}
    >
      <IntlServiceInitializer />
      {children}
    </I18nWrapper>
  );
};
